// RUTA: src/pages/Index.tsx

import { useState, useEffect } from 'react';
import { Navbar } from '@/components/fitness/Navbar';
import { DashboardSection, CardioGoalCard } from '@/components/fitness/DashboardSection';
import { SectionHeader } from '@/components/fitness/SectionHeader';
import { Flame } from 'lucide-react';
import { HistoryTable } from '@/components/fitness/HistoryTable';
import { MealPlanSection } from '@/components/fitness/MealPlanSection';
import { RoutineSection } from '@/components/fitness/RoutineSection';
import { EvaluationSection } from '@/components/fitness/EvaluationSection';
import { SupplementsSection } from '@/components/fitness/SupplementsSection';
import { CalorieTrackingSection } from '@/components/fitness/CalorieTrackingSection';
import { ExerciseLogCard } from '@/components/fitness/ExerciseLogCard';
import { CardioHistorySection } from '@/components/fitness/CardioHistorySection';
import { CardioCoachCard } from '@/components/fitness/CardioCoachCard';
import { MarcoAssistantSection } from '@/components/fitness/MarcoAssistantSection';
import { ProgressPhotosSection } from '@/components/fitness/ProgressPhotosSection';
import { BillingSection } from '@/components/fitness/BillingSection';
import { EnableNotificationsCard } from '@/components/fitness/EnableNotificationsCard';
import { DailyBriefingCard } from '@/components/fitness/DailyBriefingCard';
import { SupportFab } from '@/components/support/SupportFab';
import { StuckLoader } from '@/components/fitness/StuckLoader';
import { Footer } from '@/components/fitness/Footer';
import { SectionStickyIndicator } from '@/components/fitness/SectionStickyIndicator';
import { useBodyMeasurements } from '@/hooks/useBodyMeasurements';
import { useClientProfile } from '@/hooks/useClientProfile';
import { useClientAccess } from '@/hooks/useClientAccess';
import { useAuth } from '@/hooks/useAuth';
import { useUserRole } from '@/hooks/useUserRole';
import { AccessBlockedScreen } from '@/components/fitness/AccessBlockedScreen';
import { supabase } from '@/integrations/supabase/client';
import { getTodayLocalString } from '@/lib/dateUtils';

async function registerActivity(userId: string) {
  try {
    const today = getTodayLocalString();
    const { data: existing } = await supabase
      .from('client_activity_log')
      .select('id')
      .eq('client_id', userId)
      .eq('activity_date', today)
      .maybeSingle();
    if (!existing) {
      await supabase
        .from('client_activity_log')
        .insert({ client_id: userId, activity_type: 'login', activity_date: today });
    }
  } catch (error) {
    console.error('Error registering activity:', error);
  }
}

export default function FitnessPortal() {
  const { user } = useAuth();
  // El tracker de PWA standalone vive en App.tsx para que dispare
  // sin importar qué vista esté activa (cliente / coach / admin).
  const { isCoachOrAdmin, loading: rolesLoading } = useUserRole();
  // Lifecycle gate. The hook always runs (no conditional hooks!) but we
  // ignore its result for coaches/admins below — they aren't clients
  // of anyone, so client_access_status would always say 'no_coach'
  // and lock them out.
  const access = useClientAccess(user?.id);
  const { profile, activeUser: dynamicUser, loading: profileLoading, refetchProfile } = useClientProfile();
  const [hasAnimated, setHasAnimated] = useState(false);
  const [exerciseLogTrigger, setExerciseLogTrigger] = useState(0);
  const [cardioKcal, setCardioKcal] = useState<number | undefined>(undefined);

  useEffect(() => {
    const timer = setTimeout(() => setHasAnimated(true), 1500);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (user?.id) registerActivity(user.id);
  }, [user?.id]);

  const activeUser = dynamicUser;
  const perfil = profile;

  const { historial, refetch } = useBodyMeasurements(
    perfil?.historial || [],
    activeUser
  );

  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        refetch();
        refetchProfile();
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [refetch, refetchProfile]);

  // Hold on the spinner until we know the role. Coaches/admins skip
  // the access gate entirely; only clients land on the block screen.
  // After 8 seconds, surface a diagnostic + escape so the user is
  // never stuck on a blank spinner (we got reports of iOS Safari
  // hanging here after the SW update).
  if (rolesLoading || access.loading || profileLoading) {
    return <StuckLoader rolesLoading={rolesLoading} accessLoading={access.loading} profileLoading={profileLoading} />;
  }

  if (!isCoachOrAdmin && !access.allowed) {
    return (
      <AccessBlockedScreen
        reason={access.reason}
        coachId={access.coachId}
        pauseUntil={access.pauseUntil}
        pauseReason={access.pauseReason}
      />
    );
  }

  if (!perfil) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="w-8 h-8 border-4 border-accent/30 border-t-accent rounded-full animate-spin" />
      </div>
    );
  }

  const ultimoRegistro = historial[historial.length - 1] || {
    sesion: 'Baseline', fecha: '--', peso: 0, grasa: 0,
  };

  const grasaFaltante = (ultimoRegistro.grasa - perfil.metaGrasa).toFixed(1);

  const scrollTo = (id: string) => {
    const element = document.getElementById(id);
    if (element) element.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-background text-foreground font-sans">
      <Navbar activeUser={activeUser} perfil={perfil} onNavigate={scrollTo} />
      <SectionStickyIndicator />
      <main className={`pt-28 md:pt-32 pb-20 px-3 md:px-8 max-w-7xl mx-auto space-y-8 md:space-y-12 ${!hasAnimated ? 'stagger-children' : ''}`}>
        {/* Briefing diario tipo asistente: saludo, racha, chips de
            acciones del día (rutina, comidas, calorías) y recordatorios
            condicionales (cardio, peso, foto). Es lo primero que ve el
            cliente al abrir la app. */}
        <DailyBriefingCard
          userId={user?.id}
          displayName={activeUser}
          onNavigate={scrollTo}
          dailyCalorieGoal={perfil.metaCalorias}
          dailyCardioGoal={perfil.metaCardioKcal}
          isOwnProfile={true}
        />

        <EnableNotificationsCard />
        {/* BookTrainingCard se movió al Navbar como botón con ícono.
            Se usa puntualmente para reservar; no necesita ocupar
            espacio permanente en el home. */}
        <DashboardSection
          perfil={{ ...perfil, historial }}
          ultimoRegistro={ultimoRegistro}
          grasaFaltante={grasaFaltante}
          userId={user?.id}
          displayName={activeUser}
          isOwnProfile={true}
        />
        <HistoryTable historial={historial} onDeleted={refetch} />
        {perfil.rutina && (
          <RoutineSection
            rutina={perfil.rutina}
            activeUser={activeUser}
            onCardioLogged={() => setExerciseLogTrigger(t => t + 1)}
          />
        )}
        {perfil.metaCardioKcal && perfil.metaCardioKcal > 0 && (
          <div id="cardio" className="space-y-6 scroll-mt-32">
            <SectionHeader title="Cardio" subtitle="Meta Diaria de Quema" icon={Flame} helpId="cardio" />
            {/* "Marco dice" arriba del aro de cardio — consistencia
                con la sección de Nutrición. */}
            <CardioCoachCard userId={user?.id} profileName={activeUser} refreshKey={exerciseLogTrigger} />
            <CardioGoalCard meta={perfil.metaCardioKcal} userId={user?.id} cardioKcal={cardioKcal} />
            <ExerciseLogCard
              activeUser={activeUser}
              userId={user?.id}
              refreshTrigger={exerciseLogTrigger}
              onLogged={() => setExerciseLogTrigger(t => t + 1)}
              onCardioKcalChange={setCardioKcal}
            />
            {user?.id && (
              <CardioHistorySection
                userId={user.id}
                profileName={activeUser}
                dailyGoal={perfil.metaCardioKcal}
                refreshKey={exerciseLogTrigger}
              />
            )}
          </div>
        )}
        <CalorieTrackingSection perfil={perfil} activeUser={activeUser} refreshTrigger={exerciseLogTrigger} />
        {perfil.planAlimenticio && (
          <MealPlanSection
            plan={perfil.planAlimenticio}
            activeUser={activeUser}
            userId={user?.id}
            onCaloriesLogged={() => setExerciseLogTrigger(t => t + 1)}
          />
        )}
        <ProgressPhotosSection activeUser={activeUser} />
        <EvaluationSection
          ultimoRegistro={ultimoRegistro}
          activeUser={activeUser}
          onDataExtracted={refetch}
        />
        <MarcoAssistantSection activeUser={activeUser} />
        <SupplementsSection suplementos={perfil.suplementos} />
        <BillingSection userId={user?.id} />
      </main>
      <SupportFab />
      <Footer />
    </div>
  );
}
